import { useCallback, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { auditReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, CountChip, Pager, TabBar, Dash, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type {
  ApprovalHistoryFilter, ApprovalHistoryReportDto, ApprovalHistoryRow,
  ReversalRegisterFilter, ReversalRegisterReportDto, ReversalRegisterRow,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'APPROVAL_HISTORY';

/** Reversals landing this close to the original posting deserve a second look. */
const QUICK_REVERSAL_DAYS = 3;

const ACTION_OPTIONS = [
  { value: '',        label: 'All Actions' },
  { value: 'SUBMIT',  label: 'Submit' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'REJECT',  label: 'Reject' },
  { value: 'POST',    label: 'Post' },
];

const ACTION_TONES: Record<string, PillTone> = {
  SUBMIT:  'blue',
  APPROVE: 'green',
  REJECT:  'red',
  POST:    'purple',
};

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Export column sets ────────────────────────────────────────────────────────

const APPROVAL_COLUMNS: ExcelColumn[] = [
  { key: 'Journal',  header: 'Journal No',     width: 18 },
  { key: 'Date',     header: 'Journal Date',   width: 14 },
  { key: 'Action',   header: 'Action',         width: 12 },
  { key: 'From',     header: 'From Status',    width: 18 },
  { key: 'To',       header: 'To Status',      width: 18 },
  { key: 'By',       header: 'By',             width: 22 },
  { key: 'At',       header: 'At',             width: 20 },
  { key: 'Remarks',  header: 'Remarks',        width: 36 },
  { key: 'Response', header: 'Response Hours', width: 14, numFmt: '#,##0.00' },
];

function buildApprovalRows(rows: ApprovalHistoryRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Journal:  r.documentNumber,
    Date:     r.journalDate,
    Action:   r.action,
    From:     r.fromStatus,
    To:       r.toStatus,
    By:       r.byName ?? '',
    At:       r.actionDate,
    Remarks:  r.remarks ?? '',
    Response: r.responseHours ?? '',
  }));
}

const REVERSAL_COLUMNS: ExcelColumn[] = [
  { key: 'OrigNo',     header: 'Original No',     width: 18 },
  { key: 'OrigDate',   header: 'Original Date',   width: 14 },
  { key: 'OrigPeriod', header: 'Original Period', width: 14 },
  { key: 'OrigAmt',    header: 'Original Amount', width: 16, numFmt: '#,##0.00' },
  { key: 'OrigStatus', header: 'Original Status', width: 14 },
  { key: 'RevNo',      header: 'Reversal No',     width: 18 },
  { key: 'RevDate',    header: 'Reversal Date',   width: 14 },
  { key: 'RevPeriod',  header: 'Reversal Period', width: 14 },
  { key: 'Days',       header: 'Days Between',    width: 12 },
  { key: 'Reason',     header: 'Reason',          width: 36 },
  { key: 'RevBy',      header: 'Reversed By',     width: 22 },
  { key: 'ApprBy',     header: 'Approved By',     width: 22 },
];

function buildReversalRows(rows: ReversalRegisterRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    OrigNo:     r.originalNumber,
    OrigDate:   r.originalDate,
    OrigPeriod: r.originalPeriodCode,
    OrigAmt:    r.originalAmount,
    OrigStatus: r.originalStatus,
    RevNo:      r.reversalNumber,
    RevDate:    r.reversalDate,
    RevPeriod:  r.reversalPeriodCode,
    Days:       r.daysBetween,
    Reason:     r.reason ?? '',
    RevBy:      r.reversedByName ?? '',
    ApprBy:     r.approvedByName ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

type Tab = 'approvals' | 'reversals';

export const ApprovalHistoryPage = () => {
  const { hasPermission } = usePermission();

  const [tab, setTab] = useState<Tab>('approvals');

  // Approvals tab state
  const [apprFilter, setApprFilter] = useState<ApprovalHistoryFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [apprApplied, setApprApplied] = useState<ApprovalHistoryFilter | null>(null);
  const [apprData, setApprData] = useState<ApprovalHistoryReportDto | null>(null);
  const [apprLoading, setApprLoading] = useState(false);
  const [apprError, setApprError] = useState<string | null>(null);

  // Reversals tab state
  const [revFilter, setRevFilter] = useState<ReversalRegisterFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [revApplied, setRevApplied] = useState<ReversalRegisterFilter | null>(null);
  const [revData, setRevData] = useState<ReversalRegisterReportDto | null>(null);
  const [revLoading, setRevLoading] = useState(false);
  const [revError, setRevError] = useState<string | null>(null);

  const fetchApprovals = useCallback(async (base: ApprovalHistoryFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setApprLoading(true);
    setApprError(null);
    try {
      const res = await auditReportApi.getApprovalHistory(query);
      setApprData(res.data?.data ?? null);
      setApprApplied(query);
    } catch (e: unknown) {
      setApprError(errMessage(e));
    } finally {
      setApprLoading(false);
    }
  }, []);

  const fetchReversals = useCallback(async (base: ReversalRegisterFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setRevLoading(true);
    setRevError(null);
    try {
      const res = await auditReportApi.getReversals(query);
      setRevData(res.data?.data ?? null);
      setRevApplied(query);
    } catch (e: unknown) {
      setRevError(errMessage(e));
    } finally {
      setRevLoading(false);
    }
  }, []);

  if (!hasPermission(Permissions.RPT_USER_ACTIVITY)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const apprPage = apprData?.rows;
  const apprItems = apprPage?.items ?? [];
  const revPage = revData?.rows;
  const revItems = revPage?.items ?? [];

  const hasData = tab === 'approvals' ? !!apprData : !!revData;
  const rowCount = tab === 'approvals' ? apprItems.length : revItems.length;

  const handleExcel = () => {
    if (tab === 'approvals') {
      if (!apprData || !apprPage) return;
      exportToExcel({
        sheetName: 'Approval History',
        columns: APPROVAL_COLUMNS,
        rows: buildApprovalRows(apprItems),
        title: 'Approval History',
        subtitle: `${apprData.fromDate} to ${apprData.toDate} · Page ${apprPage.pageNumber} of ${apprPage.totalPages}`,
      }, `ApprovalHistory_${apprData.fromDate}_${apprData.toDate}_p${apprPage.pageNumber}`);
    } else {
      if (!revData || !revPage) return;
      exportToExcel({
        sheetName: 'Reversal Register',
        columns: REVERSAL_COLUMNS,
        rows: buildReversalRows(revItems),
        title: 'Reversal Pair Register',
        subtitle: `${revData.fromDate} to ${revData.toDate} · Page ${revPage.pageNumber} of ${revPage.totalPages}`,
      }, `ReversalRegister_${revData.fromDate}_${revData.toDate}_p${revPage.pageNumber}`);
    }
  };

  const handleCsv = () => {
    if (tab === 'approvals') {
      if (!apprData || !apprPage) return;
      exportToCsvRows(APPROVAL_COLUMNS, buildApprovalRows(apprItems), `ApprovalHistory_${apprData.fromDate}_${apprData.toDate}_p${apprPage.pageNumber}`);
    } else {
      if (!revData || !revPage) return;
      exportToCsvRows(REVERSAL_COLUMNS, buildReversalRows(revItems), `ReversalRegister_${revData.fromDate}_${revData.toDate}_p${revPage.pageNumber}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardCheck size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Approval History &amp; Reversals</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={hasData}
          reportKey={REPORT_KEY}
          rowCount={rowCount}
          filterSummary={tab === 'approvals'
            ? `${apprFilter.fromDate}..${apprFilter.toDate}`
            : `${revFilter.fromDate}..${revFilter.toDate}`}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'approvals', label: 'Approval History' },
          { key: 'reversals', label: 'Reversals' },
        ]}
        active={tab}
        onChange={k => setTab(k as Tab)}
      />

      {/* ── Approvals tab ────────────────────────────────────────────────────── */}
      {tab === 'approvals' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-44">
                  <label className={LABEL_CLS}>From Date *</label>
                  <input type="date" value={apprFilter.fromDate} onChange={e => setApprFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>To Date *</label>
                  <input type="date" value={apprFilter.toDate} onChange={e => setApprFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>Action</label>
                  <select
                    value={apprFilter.action ?? ''}
                    onChange={e => setApprFilter(f => ({ ...f, action: e.target.value || undefined }))}
                    className={INPUT_CLS}
                  >
                    {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <RunReportButton
                onClick={() => { void fetchApprovals(apprFilter, 1); }}
                isLoading={apprLoading}
                disabled={!apprFilter.fromDate || !apprFilter.toDate}
              />
            </div>
          </div>

          {apprError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {apprError}
            </div>
          )}

          {apprLoading && !apprData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!apprData && !apprLoading && (
            <EmptyState message='Set the date range and click "Run Report" to see who approved what, and how fast.' />
          )}

          {apprData && apprPage && (
            <div className="flex flex-col gap-4">
              {/* Summary */}
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn(CARD_CLS, 'px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300')}>
                  Avg response{' '}
                  <span className="font-bold tabular-nums text-gray-900 dark:text-white">
                    {apprData.summary.averageResponseHours === null
                      ? '—'
                      : `${apprData.summary.averageResponseHours.toFixed(1)} h`}
                  </span>
                </span>
                {apprData.summary.actionCounts.map(c => (
                  <CountChip key={c.action} label={c.action} count={c.count} tone={ACTION_TONES[c.action] ?? 'grey'} />
                ))}
              </div>

              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[1200px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={cn(TH_CLS, 'w-40')}>Journal No</th>
                        <th className={cn(TH_CLS, 'w-28')}>Journal Date</th>
                        <th className={cn(TH_CLS, 'w-28')}>Action</th>
                        <th className={TH_CLS}>Status Change</th>
                        <th className={cn(TH_CLS, 'w-44')}>By</th>
                        <th className={cn(TH_CLS, 'w-40')}>At</th>
                        <th className={TH_CLS}>Remarks</th>
                        <th className={TH_R_CLS}>Response Hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apprItems.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No approval actions match the selected criteria.
                          </td>
                        </tr>
                      ) : apprItems.map((r, i) => (
                        <tr key={`${r.journalId}-${r.actionDate}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.documentNumber}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.journalDate)}</td>
                          <td className="px-3 py-2">
                            <Pill label={r.action} tone={ACTION_TONES[r.action] ?? 'grey'} />
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {r.fromStatus}
                            <span className="mx-1.5 text-gray-400">→</span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{r.toStatus}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.byName ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(r.actionDate)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.remarks ?? <Dash />}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {r.responseHours === null ? <Dash /> : r.responseHours.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  pageNumber={apprPage.pageNumber}
                  totalPages={apprPage.totalPages}
                  totalCount={apprPage.totalCount}
                  shownCount={apprItems.length}
                  loading={apprLoading}
                  onPage={p => { void fetchApprovals(apprApplied ?? apprFilter, p); }}
                  noun="actions"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Reversals tab ────────────────────────────────────────────────────── */}
      {tab === 'reversals' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-44">
                  <label className={LABEL_CLS}>From Date *</label>
                  <input type="date" value={revFilter.fromDate} onChange={e => setRevFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>To Date *</label>
                  <input type="date" value={revFilter.toDate} onChange={e => setRevFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-52">
                  <label className={LABEL_CLS}>Max Days Between (optional)</label>
                  <input
                    type="number"
                    min={0}
                    value={revFilter.maxDaysBetween ?? ''}
                    placeholder="e.g. 3 — quick reversals only"
                    onChange={e => setRevFilter(f => ({
                      ...f,
                      maxDaysBetween: e.target.value === '' ? undefined : Number(e.target.value),
                    }))}
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              <RunReportButton
                onClick={() => { void fetchReversals(revFilter, 1); }}
                isLoading={revLoading}
                disabled={!revFilter.fromDate || !revFilter.toDate}
              />
            </div>
          </div>

          {revError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {revError}
            </div>
          )}

          {revLoading && !revData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!revData && !revLoading && (
            <EmptyState message='Set the date range and click "Run Report" to list original/reversal journal pairs.' />
          )}

          {revData && revPage && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Reversal Pairs</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {fmtDate(revData.fromDate)} to {fmtDate(revData.toDate)}
                </span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1500px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={cn(TH_CLS, 'w-40')}>Original No</th>
                      <th className={cn(TH_CLS, 'w-28')}>Original Date</th>
                      <th className={cn(TH_CLS, 'w-24')}>Period</th>
                      <th className={TH_R_CLS}>Amount</th>
                      <th className={cn(TH_CLS, 'w-28')}>Status</th>
                      <th className={cn(TH_CLS, 'w-40')}>Reversal No</th>
                      <th className={cn(TH_CLS, 'w-28')}>Reversal Date</th>
                      <th className={cn(TH_CLS, 'w-24')}>Period</th>
                      <th className={TH_R_CLS}>Days Between</th>
                      <th className={TH_CLS}>Reason</th>
                      <th className={cn(TH_CLS, 'w-36')}>Reversed By</th>
                      <th className={cn(TH_CLS, 'w-36')}>Approved By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revItems.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No reversal pairs match the selected criteria.
                        </td>
                      </tr>
                    ) : revItems.map(r => (
                      <tr key={r.reversalJournalId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.originalNumber}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.originalDate)}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.originalPeriodCode}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.originalAmount)}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.originalStatus}</td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.reversalNumber}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.reversalDate)}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.reversalPeriodCode}</td>
                        <td
                          className={cn(
                            AMT_CLS,
                            r.daysBetween <= QUICK_REVERSAL_DAYS
                              ? 'font-bold text-red-600 dark:text-red-400'
                              : 'text-gray-700 dark:text-gray-300',
                          )}
                          title={r.daysBetween <= QUICK_REVERSAL_DAYS ? 'Reversed shortly after posting' : undefined}
                        >
                          {r.daysBetween}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.reason ?? <Dash />}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.reversedByName ?? <Dash />}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.approvedByName ?? <Dash />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                pageNumber={revPage.pageNumber}
                totalPages={revPage.totalPages}
                totalCount={revPage.totalCount}
                shownCount={revItems.length}
                loading={revLoading}
                onPage={p => { void fetchReversals(revApplied ?? revFilter, p); }}
                noun="pairs"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
